import { Box, Link, Stack, Tooltip, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Pill } from '../../../shared/components/Pill';
import { SEMANTIC_COLOR_VARS, hashSemanticColor } from '../../../shared/components/semanticColor';
import { formatEntityTypeLabel } from '../../../shared/components/entityTypeLabel';
import type { EntityMarketFeed } from '../../../core/types/data-models';
import { MARKET_FEED_IMPORTANCE_COLOR } from '../marketFeedImportance';

/** Timeline geometry: rail centre and the gutter the entries are indented by — MarketRadarSection draws the rail to match. */
export const RAIL_CENTER_PX = 6;
export const RAIL_GUTTER_PX = 24;

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** One signal on the timeline — entity, headline, summary, and the way into its Market Feed tab. */
export function MarketRadarEntry({ item, onOpen }: { item: EntityMarketFeed; onOpen: () => void }) {
  const importanceVars = SEMANTIC_COLOR_VARS[MARKET_FEED_IMPORTANCE_COLOR[item.importance]];
  const typeVars = SEMANTIC_COLOR_VARS[hashSemanticColor(item.entityType)];
  const typeLabel = formatEntityTypeLabel(item.entityType);

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Rail marker — the importance colour is the timeline's only ranking cue. */}
      <Box
        sx={{
          position: 'absolute',
          left: RAIL_CENTER_PX - 5 - RAIL_GUTTER_PX,
          top: 12,
          width: 10,
          height: 10,
          borderRadius: '50%',
          bgcolor: 'background.paper',
          border: '2.5px solid',
          borderColor: importanceVars.main,
        }}
      />
      <Tooltip title={`Open ${typeLabel} market feed`} placement="left">
        <Box
          onClick={onOpen}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            minWidth: 0,
            p: 1.25,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            transition: 'border-color var(--transition)',
            '&:hover': { borderColor: importanceVars.line },
            '&:hover .radar-open-arrow': { opacity: 1 },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              noWrap
              sx={{ color: typeVars.main, flex: 1, minWidth: 0 }}
            >
              {item.entityName}
            </Typography>
            <Pill color={MARKET_FEED_IMPORTANCE_COLOR[item.importance]} sx={{ flexShrink: 0 }}>
              {item.importance}
            </Pill>
            <ArrowForwardIcon
              className="radar-open-arrow"
              sx={{ fontSize: 14, color: 'text.secondary', opacity: 0, flexShrink: 0, transition: 'opacity 0.15s' }}
            />
          </Stack>

          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.35,
            }}
          >
            {item.resourceName}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.45,
            }}
          >
            {item.description}
          </Typography>

          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.25, minWidth: 0 }}>
            <Typography variant="caption" color="text.disabled" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {typeLabel} · {formatTime(item.publishedAt)}
            </Typography>
            <Link
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              variant="caption"
              // The whole card navigates to the Market Feed tab; this one link goes elsewhere.
              onClick={(e) => e.stopPropagation()}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, flexShrink: 0 }}
            >
              Source <OpenInNewIcon sx={{ fontSize: 12 }} />
            </Link>
          </Stack>
        </Box>
      </Tooltip>
    </Box>
  );
}
